import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import api, { formatApiError } from "@/services/api";

export default function AdminResetPassword() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Lyen an pa valab");
      return;
    }
    if (password.length < 8) {
      toast.error("Modpas la dwe gen omwen 8 karaktè");
      return;
    }
    if (password !== confirm) {
      toast.error("Modpas yo pa menm");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        new_password: password,
      });
      toast.success(data?.message || "Modpas chanje");
      navigate("/admin/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erè");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-brand-blue/20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-brand-gold/10 blur-3xl" />
      </div>
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="flex justify-center mb-8">
          <Logo size={56} />
        </div>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-2xl">
          <h1 className="font-heading text-2xl font-bold">{t("reset_password_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("reset_password_sub")}</p>

          {!token ? (
            <div className="mt-6 text-sm text-muted-foreground space-y-3">
              <p>Lyen an pa valab. Mande yon nouvo lyen.</p>
              <Link to="/admin/forgot-password" className="text-brand-blue hover:underline">
                {t("forgot_password")}
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="npw">{t("new_password")}</Label>
                <div className="mt-1.5 relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="npw"
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password-input"
                    className="pl-9 h-11"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="cpw">{t("confirm_password")}</Label>
                <div className="mt-1.5 relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="cpw"
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-password-confirm"
                    className="pl-9 h-11"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-btn"
                className="w-full h-11 bg-brand-blue hover:bg-brand-blueHover text-white font-semibold"
              >
                {loading ? t("loading") : t("save_password")}
              </Button>
              <div className="text-center text-xs">
                <Link to="/admin/login" className="text-muted-foreground hover:text-foreground">
                  ← {t("back_to_login")}
                </Link>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
